import React, { useState, useRef } from "react";
import { musicTracks } from "../../data/tracks";
import "./MusicGallery.css";

const MusicGallery = () => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [progress, setProgress] = useState({});
  const audioRefs = useRef([]);

  const togglePlay = (index) => {
    const audio = audioRefs.current[index];

    if (audio.paused) {
      // Pause all others
      audioRefs.current.forEach((a, i) => {
        if (i !== index) a.pause();
      });
      audio.play();
      setCurrentTrack(index);
    } else {
      audio.pause();
      setCurrentTrack(null);
    }
  };

  const handleTimeUpdate = (index) => {
    const audio = audioRefs.current[index];
    if (audio) {
      const percent = (audio.currentTime / audio.duration) * 100 || 0;
      setProgress((prev) => ({ ...prev, [index]: percent }));
    }
  };

  const handleSeek = (index, e) => {
    const audio = audioRefs.current[index];
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audio.currentTime = percent * audio.duration;
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="music-gallery-container">
      <div className="music-header">
        <h1 className="music-title">Music Collection</h1>
        <p className="music-subtitle">Original compositions and covers</p>
      </div>

      <div className="music-gallery">
        {musicTracks.map((track, index) => {
          const audio = audioRefs.current[index];
          const isPlaying = currentTrack === index;
          const currentProgress = progress[index] || 0;

          return (
            <div
              className={`track-card ${isPlaying ? "playing" : ""}`}
              key={track.file}
            >
              <div className="track-header">
                <div className="track-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                  </svg>
                </div>
                <h3 className="track-title">{track.title}</h3>
              </div>

              <div className="track-controls">
                <button
                  className="play-button"
                  onClick={() => togglePlay(index)}
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>

                <div className="track-info">
                  <div
                    className="progress-bar"
                    onClick={(e) => handleSeek(index, e)}
                  >
                    <div
                      className="progress-fill"
                      style={{ width: `${currentProgress}%` }}
                    />
                  </div>

                  <div className="time-display">
                    <span className="time-current">
                      {formatTime(audio?.currentTime || 0)}
                    </span>
                    <span className="time-total">
                      {formatTime(audio?.duration || 0)}
                    </span>
                  </div>
                </div>
              </div>

              <audio
                ref={(el) => (audioRefs.current[index] = el)}
                src={track.file}
                onTimeUpdate={() => handleTimeUpdate(index)}
                onEnded={() => setCurrentTrack(null)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MusicGallery;
